import { useEffect, useRef, useState, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import { Crop } from '@mui/icons-material';

/**
 * AreaCaptureOverlay
 *
 * Full-viewport drag-select overlay. Student drags a rectangle over any part of the
 * screen. On release, all <img> elements that overlap the selection are composited
 * onto the cropped area, and onCapture(jpegDataUrl) is called with the result.
 *
 * No containerRef needed — images are discovered directly from the DOM.
 *
 * Props:
 *   onCapture  — callback(croppedDataUrl: string) — receives a JPEG of the selected area
 *   onCancel   — called on Escape or tiny (accidental) drag
 */

export default function AreaCaptureOverlay({ onCapture, onCancel }) {
  const [selection, setSelection] = useState(null); // { x1, y1, x2, y2 } in viewport px
  const [capturing, setCapturing] = useState(false);
  const startRef = useRef(null);
  const overlayRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onCancel?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  const handlePointerDown = useCallback((e) => {
    e.preventDefault();
    overlayRef.current?.setPointerCapture(e.pointerId);
    startRef.current = { x: e.clientX, y: e.clientY };
    setSelection({ x1: e.clientX, y1: e.clientY, x2: e.clientX, y2: e.clientY });
  }, []);

  const handlePointerMove = useCallback((e) => {
    if (!startRef.current) return;
    setSelection({ x1: startRef.current.x, y1: startRef.current.y, x2: e.clientX, y2: e.clientY });
  }, []);

  const handlePointerUp = useCallback(async (e) => {
    if (!startRef.current) return;
    const start = startRef.current;
    startRef.current = null;

    const selW = Math.abs(e.clientX - start.x);
    const selH = Math.abs(e.clientY - start.y);
    if (selW < 10 || selH < 10) {
      setSelection(null);
      onCancel?.();
      return;
    }

    // Selection in viewport CSS coords
    const selLeft   = Math.min(start.x, e.clientX);
    const selTop    = Math.min(start.y, e.clientY);
    const selRight  = Math.max(start.x, e.clientX);
    const selBottom = Math.max(start.y, e.clientY);

    setCapturing(true);
    setSelection(null);

    try {
      const cssW = Math.round(selRight - selLeft);
      const cssH = Math.round(selBottom - selTop);
      const dpr = Math.min(window.devicePixelRatio || 1, 2);

      // Use html2canvas to capture the selected viewport area — handles HTML tables and all DOM content.
      // The overlay element itself must be excluded; otherwise its rgba(0,0,0,0.30) background
      // tints the entire capture gray.
      // allowTaint: true prevents silent blank capture when tables contain cross-origin images.
      // scale at devicePixelRatio produces a sharp image on HiDPI displays.
      const overlayEl = overlayRef.current;
      const { default: html2canvas } = await import('html2canvas');
      const captured = await html2canvas(document.body, {
        x: selLeft + window.scrollX,
        y: selTop + window.scrollY,
        width: cssW,
        height: cssH,
        scrollX: -window.scrollX,
        scrollY: -window.scrollY,
        scale: dpr,
        useCORS: true,
        allowTaint: true,
        imageTimeout: 0,
        logging: false,
        backgroundColor: '#ffffff',
        ignoreElements: (el) => el === overlayEl,
      });

      const pixW = captured.width;
      const pixH = captured.height;
      const final = document.createElement('canvas');
      final.width = pixW;
      final.height = pixH;
      const fctx = final.getContext('2d');
      fctx.fillStyle = '#ffffff';
      fctx.fillRect(0, 0, pixW, pixH);
      fctx.drawImage(captured, 0, 0, pixW, pixH);

      onCapture(final.toDataURL('image/jpeg', 0.92));
    } catch {
      onCancel?.();
    } finally {
      setCapturing(false);
    }
  }, [onCapture, onCancel]);

  const selRect = selection
    ? {
        left:   Math.min(selection.x1, selection.x2),
        top:    Math.min(selection.y1, selection.y2),
        width:  Math.abs(selection.x2 - selection.x1),
        height: Math.abs(selection.y2 - selection.y1),
      }
    : null;

  return (
    <Box
      ref={overlayRef}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 2000,
        cursor: capturing ? 'wait' : 'crosshair',
        bgcolor: 'rgba(0,0,0,0.30)',
        userSelect: 'none',
        touchAction: 'none',
      }}
    >
      {!capturing && (
        <Box
          sx={{
            position: 'absolute',
            top: 20,
            left: '50%',
            transform: 'translateX(-50%)',
            bgcolor: 'rgba(0,0,0,0.78)',
            color: 'white',
            px: 3,
            py: 1.25,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            pointerEvents: 'none',
            whiteSpace: 'nowrap',
          }}
        >
          <Crop sx={{ fontSize: 18 }} />
          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.82rem' }}>
            Drag to select the question image area — Esc to cancel
          </Typography>
        </Box>
      )}

      {capturing && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'rgba(0,0,0,0.78)',
            color: 'white',
            px: 3,
            py: 1.5,
            borderRadius: 2,
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600 }}>Capturing…</Typography>
        </Box>
      )}

      {selRect && selRect.width > 2 && selRect.height > 2 && (
        <Box
          sx={{
            position: 'fixed',
            left: selRect.left,
            top: selRect.top,
            width: selRect.width,
            height: selRect.height,
            border: '2px solid #2196f3',
            bgcolor: 'rgba(33,150,243,0.10)',
            pointerEvents: 'none',
          }}
        />
      )}
    </Box>
  );
}
